import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-[#111827] mb-1.5">
      {label} {required && <span className="text-[#DC2626]">*</span>}
    </label>
    {children}
  </div>
);

export default function CompanyForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: "", company_title: "", address: "", tax_office: "", tax_number: "",
    landline_phone: "", mobile_phone: "", email: "", contact_person: "",
    service_area_id: "", payment_term_days: 30, description: "",
    assigned_personnel_name: "",
  });

  useEffect(() => {
    api.get("/definitions/service_areas").then((r) => setAreas(r.data.filter(a => a.is_active)));
    if (isEdit) {
      api.get(`/companies/${id}`).then((r) => {
        const { summary, ...rest } = r.data;
        setForm(rest);
      });
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, payment_term_days: Number(form.payment_term_days) };
      if (isEdit) {
        await api.put(`/companies/${id}`, payload);
        toast.success("Firma güncellendi");
      } else {
        await api.post("/companies", payload);
        toast.success("Firma oluşturuldu");
      }
      navigate("/companies");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-5xl space-y-5">
      <div className="azd-card p-6">
        <div className="text-sm font-bold text-[#08285A] mb-4 uppercase tracking-wider">
          Firma Bilgileri
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Firma Adı" required>
            <Input data-testid="company-name" value={form.company_name}
                   onChange={(e) => set("company_name", e.target.value)} required />
          </Field>
          <Field label="Firma Ünvanı" required>
            <Input data-testid="company-title" value={form.company_title}
                   onChange={(e) => set("company_title", e.target.value)} required />
          </Field>
          <div className="md:col-span-2">
            <Field label="Adres" required>
              <Textarea value={form.address} onChange={(e) => set("address", e.target.value)}
                        rows={2} required data-testid="company-address" />
            </Field>
          </div>
          <Field label="Vergi Dairesi" required>
            <Input value={form.tax_office} onChange={(e) => set("tax_office", e.target.value)}
                   required data-testid="company-tax-office" />
          </Field>
          <Field label="Vergi Numarası" required>
            <Input value={form.tax_number} onChange={(e) => set("tax_number", e.target.value)}
                   required data-testid="company-tax-no" />
          </Field>
          <Field label="Sabit Telefon">
            <Input value={form.landline_phone} onChange={(e) => set("landline_phone", e.target.value)} />
          </Field>
          <Field label="Cep Telefonu" required>
            <Input value={form.mobile_phone} onChange={(e) => set("mobile_phone", e.target.value)}
                   required data-testid="company-phone" />
          </Field>
          <Field label="E-Posta" required>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                   required data-testid="company-email" />
          </Field>
          <Field label="İlgili Kişi" required>
            <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)}
                   required data-testid="company-contact" />
          </Field>
          <Field label="Hizmet Alanı" required>
            <Select value={form.service_area_id} onValueChange={(v) => set("service_area_id", v)}>
              <SelectTrigger data-testid="company-area"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {areas.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Cari Vade Süresi (Gün)" required>
            <Input type="number" min="0" value={form.payment_term_days}
                   onChange={(e) => set("payment_term_days", e.target.value)} required
                   data-testid="company-vade" />
          </Field>
          <Field label="Firmayı Takip Eden Personel (opsiyonel)">
            <Input value={form.assigned_personnel_name || ""}
                   onChange={(e) => set("assigned_personnel_name", e.target.value)}
                   placeholder="Ad Soyad"
                   data-testid="company-assigned-personnel" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Açıklama">
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} />
            </Field>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate("/companies")}>Vazgeç</Button>
        <Button type="submit" disabled={saving} data-testid="save-company"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : isEdit ? "Güncelle" : "Kaydet"}
        </Button>
      </div>
    </form>
  );
}
